import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Mail, MapPin, Clock } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { SITE } from "../lib/site";
import { useSite } from "../lib/SiteContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  interest: "general",
  subject: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const { config } = useSite();
  const c = config.contact || {};

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        subject: form.subject || "General inquiry",
      });
      toast.success("Thanks! We'll reply soon.");
      setForm(initial);
    } catch (err) {
      console.error(err);
      toast.error("Couldn't send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-28 bg-[#EAE5DF]"
    >
      <div className="mx-auto max-w-6xl px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-8">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#D85C41]">
                Get in touch
              </span>
              <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold leading-[1.05] text-[#1C1917]">
                Volunteer, partner, or just say hi.
              </h2>
              <p className="mt-5 text-base md:text-lg text-[#57534E]">
                Schools, shelters, faith groups, and businesses — we'd love to
                hear from you. Pickup partnerships and detergent drives welcome.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white border border-[#D6D3D1]">
                  <Mail className="h-5 w-5 text-[#D85C41]" strokeWidth={2} />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#78716C]">Email</p>
                  <a
                    href={`mailto:${SITE.email}`}
                    className="text-base font-semibold text-[#1C1917] hover:text-[#D85C41]"
                  >
                    {SITE.email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white border border-[#D6D3D1]">
                  <MapPin className="h-5 w-5 text-[#D85C41]" strokeWidth={2} />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#78716C]">Where</p>
                  <p className="text-base font-semibold text-[#1C1917]">{SITE.address}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white border border-[#D6D3D1]">
                  <Clock className="h-5 w-5 text-[#D85C41]" strokeWidth={2} />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#78716C]">Hours</p>
                  <p className="text-base font-semibold text-[#1C1917]">{SITE.hours}</p>
                </div>
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 rounded-3xl bg-white border border-[#E0DCD3] p-7 md:p-10 space-y-5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-[#1C1917] mb-2">Name</label>
                <input
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="w-full rounded-xl border border-[#D6D3D1] bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D85C41]/40 focus:border-[#D85C41]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1C1917] mb-2">Email</label>
                <input
                  data-testid="contact-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="w-full rounded-xl border border-[#D6D3D1] bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D85C41]/40 focus:border-[#D85C41]"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-[#1C1917] mb-2">I'm reaching out as</label>
                <Select
                  value={form.interest}
                  onValueChange={(v) => update("interest", v)}
                >
                  <SelectTrigger
                    data-testid="contact-interest-trigger"
                    className="w-full rounded-xl border-[#D6D3D1] bg-white py-6"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General inquiry</SelectItem>
                    <SelectItem value="volunteer">Future volunteer</SelectItem>
                    <SelectItem value="partner">Shelter / school / partner</SelectItem>
                    <SelectItem value="press">Press / media</SelectItem>
                    <SelectItem value="donor">Donor / sponsor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1C1917] mb-2">Subject</label>
                <input
                  data-testid="contact-subject-input"
                  value={form.subject}
                  onChange={(e) => update("subject", e.target.value)}
                  placeholder="Optional"
                  className="w-full rounded-xl border border-[#D6D3D1] bg-white px-4 py-3 placeholder:text-[#A8A29E] focus:outline-none focus:ring-2 focus:ring-[#D85C41]/40 focus:border-[#D85C41]"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#1C1917] mb-2">Message</label>
              <textarea
                data-testid="contact-message-input"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                rows={5}
                className="w-full rounded-xl border border-[#D6D3D1] bg-white px-4 py-3 placeholder:text-[#A8A29E] focus:outline-none focus:ring-2 focus:ring-[#D85C41]/40 focus:border-[#D85C41] resize-y"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1C1917] px-7 py-3.5 text-sm font-semibold text-[#F9F7F4] hover:bg-[#D85C41] transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" strokeWidth={2.5} />
                  Send Message
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
